import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Box, Card, CardContent, Link, Typography } from '@material-ui/core';
import TableLoader from '../TableLoader';
import listingStyle from '../../theme/styles/pages/Listing';
import { getFilmmakerFilmsAnalytics } from '../../services/Filmmakers';
import SnackbarMessage from '../SnackbarMessage';

const useStyles = makeStyles(listingStyle);

const Revenue = () => {
  const classes = useStyles();
  const intervalRef = useRef(null);

  const [data, setData] = useState({
    rows: [],
    totalRows: 0,
    totalRevenue: 0,
    totalPayouts: 0,
    futurePayouts: 0,
    totalViews: 0,
    totalWatchedMinutes: 0,
    page: 1,
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [snackbarMeesage, setSnackbarMeesage] = useState({
    show: false,
    type: '',
    message: '',
  });

  const columns = [
    { name: 'Program', id: 'program', width: '15%' },
    { name: 'Views', id: 'title', width: '10%', align: 'center' },
    { name: 'View Minutes', id: 'viewMinutes', width: '10%', align: 'center' },
    { name: 'Revenue', id: 'revenue', width: '10%', align: 'center' },
  ];

  const fetchData = (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setDataLoaded(false);
    }

    getFilmmakerFilmsAnalytics(page)
      .then((res) => {
        setData((prev) => ({
          ...prev,
          ...res.data,
          page,
          rows: append ? [...prev.rows, ...res.data.rows] : res.data.rows,
        }));
        setDataLoaded(true);
        setLoadingMore(false);
      })
      .catch(() => {
        setSnackbarMeesage({
          message: 'Error in loading the data.',
          type: 'error',
          show: true,
        });
        setDataLoaded(true);
        setLoadingMore(false);
      });
  };

  const startAutoRefresh = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => fetchData(1), 5 * 60 * 1000);
  };

  const handleManualRefresh = () => {
    fetchData(1);
    startAutoRefresh();
  };

  const handleViewMore = () => {
    const nextPage = data.page + 1;
    fetchData(nextPage, true);
  };

  useEffect(() => {
    fetchData();
    startAutoRefresh();
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" marginTop={1}>
        <Button variant="contained" onClick={handleManualRefresh}>
          Refresh
        </Button>
      </Box>

      <Box display="flex" justifyContent="space-between" marginBottom={2} marginTop={2}>
        {[
          { title: 'Total Revenue', value: data.totalRevenue },
          { title: 'Total Payouts', value: data.totalPayouts },
          { title: 'Future Payouts', value: data.futurePayouts },
        ].map((item) => (
          <Box item key={item.title}>
            <Card style={{ backgroundColor: '#F2F4F6', width: 300, height: 80 }} elevation={1}>
              <CardContent
                style={{
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  padding: 8,
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  gutterBottom
                  style={{ fontWeight: 'bold' }}
                >
                  {item.title}
                </Typography>
                <Typography variant="h5" component="div">
                  $ {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <TableContainer>
        <Table className={classes.tableData} aria-label="revenue-table">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell width={col.width} align={col.align || 'left'} key={`col-${col.id}`}>
                  <span>{col.name}</span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!dataLoaded && data.rows.length === 0 && <TableLoader cols={4} />}

            {dataLoaded && data?.rows?.length === 0 && (
              <TableCell align="center" colSpan={4} className={classes.noRecordFoundText}>
                No records found
              </TableCell>
            )}

            {data.rows.map((row) => (
              <TableRow key={`row-${row.id}`}>
                <TableCell>
                  <Link href={row.programLink} target="_blank">
                    {row.title || '-'}
                  </Link>
                </TableCell>
                <TableCell align="center">{row.views || 0}</TableCell>
                <TableCell align="center">{row.totalWatchedMinutes || 0}</TableCell>
                <TableCell align="center">{row.revenue}</TableCell>
              </TableRow>
            ))}

            {loadingMore && <TableLoader cols={4} />}

            {dataLoaded && data.rows.length > 0 && (
              <>
                {data.rows.length < data.totalRows && !loadingMore && (
                  <TableRow>
                    <TableCell align="center" colSpan={4}>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={handleViewMore}
                        style={{ cursor: 'pointer', borderBottom: '1px solid black' }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleViewMore();
                        }}
                      >
                        <b>View all</b>
                      </span>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>
                    <b>Total</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>{data.totalViews}</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>{data.totalWatchedMinutes}</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>${data.totalRevenue}</b>
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {snackbarMeesage.show && <SnackbarMessage {...snackbarMeesage} />}
    </>
  );
};

export default Revenue;
